// @flow

import React, { PureComponent } from 'react';
import { graphql, QueryRenderer } from 'react-relay';
import { fetchQuery } from 'relay-runtime';
import styled from 'styled-components';
import moment from 'moment';
import { i18n, getCurrencySymbol } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Page from 'main-app/components/Page';
import PageHeader from 'main-app/components/PageHeader';
import Button from 'shared/components/common/Button';
import * as Actions from 'main-app/store/Actions';
import DeleteShipmentMutation from 'main-app/mutations/DeleteShipment';
import UpdateShipmentMutation from 'main-app/mutations/UpdateShipment';
import DeleteShipmentItemMutation from 'main-app/mutations/DeleteShipmentItem';
import PickShipmentItemsMutation from 'main-app/mutations/PickShipmentItems';
import AddUpdateShipmentModal from 'main-app/components/AddUpdateShipmentModal';
import AddShipmentItemModal from 'main-app/components/AddShipmentItemModal';
import ShipmentStatusPill from 'shared/components/common/Pill/ShipmentStatusPill';
import Logo from 'main-app/components/PageHeaderLogo/img/logo.svg';
import EmailShipmentMutation from 'main-app/mutations/EmailShipment';
import PurchaseUpsLabelMutation from 'main-app/mutations/PurchaseUpsLabel';
import RefreshUpsTrackingMutation from 'main-app/mutations/RefreshUpsTracking';
import BarcodeScannerInput from 'shared/components/common/BarcodeScannerInput';
import {
  PrintWrapper,
  PrintOnly,
  NoPrint,
  PrintPage,
  PrintDocHeader,
  PrintDocHeaderTitle,
  PrintDocHeaderCompany,
  PrintInfoGrid,
  PrintInfoColumn,
  PrintInfoRow,
  PrintInfoLabel,
  PrintNotes,
  PrintItemTable,
  PrintDocFooter,
  PrintSignatureGrid,
  PrintSignatureColumn,
  PrintSignatureLine,
  PrintSignatureUnderline,
} from 'shared/components/common/PrintLayout';
import type { RouterHistory, Match } from 'react-router';

const Content = styled.div`
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #666;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const DetailLabel = styled.span`
  font-size: 12px;
  color: #999;
  margin-bottom: 4px;
`;

const DetailValue = styled.span`
  font-size: 14px;
  color: #333;
`;

const ItemsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const ItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  border-bottom: 2px solid #e8e8e8;
  font-weight: 600;
  color: #666;
  font-size: 12px;
  text-transform: uppercase;
`;

const Td = styled.td`
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
  color: #333;
`;

const DeleteLink = styled.span`
  color: #ff4d4f;
  cursor: pointer;
  font-size: 13px;
  &:hover {
    text-decoration: underline;
  }
`;

const ActionBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
`;

type Props = {
  history: RouterHistory,
  match: Match,
};

type State = {
  editModalOpen: boolean,
  addItemModalOpen: boolean,
  pickingMode: boolean,
  pickQuantities: { [string]: { quantity: string, lotId: string } },
  autoPickLoading: boolean,
  upsRates: Array<Object>,
  upsRatesLoading: boolean,
  upsLabelLoading: boolean,
  upsTrackingLoading: boolean,
};

const SUGGEST_PICKS_QUERY = graphql`
  query ShipmentSuggestPicksQuery($itemId: ID!, $quantityNeeded: Float!) {
    suggestPicks(itemId: $itemId, quantityNeeded: $quantityNeeded) {
      lotId
      lotNumber
      quantity
      warehouseLocationName
    }
  }
`;

class Shipment extends PureComponent<Props, State> {
  state = {
    editModalOpen: false,
    addItemModalOpen: false,
    pickingMode: false,
    pickQuantities: {},
    autoPickLoading: false,
    refreshKey: 0,
    upsRates: [],
    upsRatesLoading: false,
    upsLabelLoading: false,
    upsTrackingLoading: false,
  };

  handleGetUpsRates = (shipmentId: string) => {
    this.setState({ upsRatesLoading: true });
    fetchQuery(relayEnvironment, graphql`
      query ShipmentUpsRatesQuery($shipmentId: ID!) {
        upsRates(shipmentId: $shipmentId) {
          serviceCode
          serviceName
          totalCharge
          currency
          estimatedDays
        }
      }
    `, { shipmentId })
      .then(result => {
        this.setState({ upsRates: result.upsRates || [], upsRatesLoading: false });
      })
      .catch(() => {
        Actions.alertNotification(i18n.t('Error fetching UPS rates. Check UPS configuration in Settings.'), 'error');
        this.setState({ upsRatesLoading: false });
      });
  };

  handlePurchaseLabel = (shipmentId: string, serviceCode: string) => {
    this.setState({ upsLabelLoading: true });
    PurchaseUpsLabelMutation.commit({
      variables: { input: { shipmentId, serviceCode } },
    })
      .then((response) => {
        const error = response && response.purchaseUpsLabel && response.purchaseUpsLabel.error;
        if (error) {
          Actions.alertNotification(error, 'error');
          this.setState({ upsLabelLoading: false });
        } else {
          Actions.alertNotification(i18n.t('UPS label purchased successfully!'), 'success');
          this.setState(prev => ({ upsLabelLoading: false, upsRates: [], refreshKey: prev.refreshKey + 1 }));
        }
      })
      .catch(() => {
        Actions.alertNotification(i18n.t('Error purchasing UPS label.'), 'error');
        this.setState({ upsLabelLoading: false });
      });
  };

  handleRefreshTracking = (shipmentId: string) => {
    this.setState({ upsTrackingLoading: true });
    RefreshUpsTrackingMutation.commit({
      variables: { input: { shipmentId } },
    })
      .then(() => {
        Actions.alertNotification(i18n.t('Tracking updated.'), 'success');
        this.setState(prev => ({ upsTrackingLoading: false, refreshKey: prev.refreshKey + 1 }));
      })
      .catch(() => {
        Actions.alertNotification(i18n.t('Error refreshing tracking.'), 'error');
        this.setState({ upsTrackingLoading: false });
      });
  };

  handleUpdatePackageDimensions = (shipmentId: string, field: string, value: string) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0) return;
    UpdateShipmentMutation.commit({
      variables: { input: { id: shipmentId, [field]: numValue } },
    }).catch(() => {
      Actions.alertNotification(i18n.t('Error updating package dimensions.'), 'error');
    });
  };

  handleDelete = (shipmentId: string) => {
    const { history } = this.props;
    DeleteShipmentMutation.commit({
      variables: { input: { id: shipmentId } },
      onCompleted: () => {
        Actions.alertNotification(i18n.t('Shipment deleted.'), 'success');
        history.push('/shipments');
      },
      onError: () => {
        Actions.alertNotification(i18n.t('Error deleting shipment.'), 'error');
      },
    });
  };

  handleDeleteItem = (shipmentItemId: string) => {
    DeleteShipmentItemMutation.commit({
      variables: { input: { id: shipmentItemId } },
    })
      .then(() => {
        Actions.alertNotification(i18n.t('Item removed from shipment.'), 'success');
        this.setState(prev => ({ refreshKey: prev.refreshKey + 1 }));
      })
      .catch(() => {
        Actions.alertNotification(i18n.t('Error removing item.'), 'error');
      });
  };

  handleMarkShipped = (shipment: Object) => {
    UpdateShipmentMutation.commit({
      variables: {
        input: {
          id: shipment.id,
          status: 'SHIPPED',
          actualShipDate: new Date().toISOString(),
        },
      },
    })
      .then((response) => {
        const error = response && response.updateShipment && response.updateShipment.error;
        if (error) {
          Actions.alertNotification(error, 'error');
        } else {
          Actions.alertNotification(i18n.t('Shipment marked as shipped.'), 'success');
        }
      })
      .catch(() => {
        Actions.alertNotification(i18n.t('Error updating shipment.'), 'error');
      });
  };

  handlePrintPackingSlip = () => {
    window.print();
  };

  handleEmailShipment = (shipmentId: string) => {
    EmailShipmentMutation.commit({
      variables: { input: { shipmentId } },
    })
      .then(() => {
        Actions.alertNotification(i18n.t('Shipment email sent successfully.'), 'success');
      })
      .catch(() => {
        Actions.alertNotification(i18n.t('Error sending shipment email.'), 'error');
      });
  };

  handleTogglePicking = () => {
    this.setState(prev => ({ pickingMode: !prev.pickingMode, pickQuantities: {}, autoPickLoading: false }));
  };

  currentItemEdges: ?Array<Object> = null;

  handleBarcodeScan = (result: Object) => {
    const itemEdges = this.currentItemEdges;
    if (!itemEdges) return;

    if (result.type === 'ITEM') {
      // Find matching shipment item by item code (itemNumber)
      const matchingEdge = itemEdges.find(
        edge =>
          !edge.node.fullyPicked &&
          (edge.node.item.itemNumber === result.code ||
            edge.node.item.id === result.id),
      );

      if (matchingEdge) {
        const node = matchingEdge.node;
        const remaining =
          parseFloat(node.quantity) - parseFloat(node.pickedQuantity || 0);
        const currentPick = this.state.pickQuantities[node.id];
        const currentQty = currentPick
          ? parseFloat(currentPick.quantity) || 0
          : 0;
        const newQty = Math.min(currentQty + 1, remaining);

        this.setState(prev => ({
          pickQuantities: {
            ...prev.pickQuantities,
            [node.id]: {
              ...(prev.pickQuantities[node.id] || {
                lotId: '',
                lotNumber: '',
                warehouseLocationName: '',
              }),
              quantity: String(newQty),
            },
          },
        }));

        Actions.alertNotification(
          i18n.t('Scanned: {{name}} (qty {{qty}})', {
            name: node.item.name,
            qty: newQty,
          }),
          'success',
        );
      } else {
        Actions.alertNotification(
          i18n.t('Item "{{code}}" not found in this shipment or already fully picked.', {
            code: result.code,
          }),
          'warning',
        );
      }
    } else if (result.type === 'LOT') {
      // Find the first unpicked lot-tracked item and set its lotId
      const matchingEdge = itemEdges.find(
        edge =>
          !edge.node.fullyPicked && edge.node.item.lotTracking,
      );

      if (matchingEdge) {
        const node = matchingEdge.node;
        this.setState(prev => ({
          pickQuantities: {
            ...prev.pickQuantities,
            [node.id]: {
              ...(prev.pickQuantities[node.id] || {
                quantity: '',
                warehouseLocationName: '',
              }),
              lotId: result.id,
              lotNumber: result.code,
            },
          },
        }));

        Actions.alertNotification(
          i18n.t('Lot {{lot}} assigned to {{item}}', {
            lot: result.code,
            item: node.item.name,
          }),
          'success',
        );
      }
    }
  };

  handleAutoPick = async (itemEdges: Array<Object>) => {
    this.setState({ autoPickLoading: true });
    const newPickQuantities = {};
    let pickedCount = 0;

    try {
      for (const edge of itemEdges) {
        const node = edge.node;
        if (node.fullyPicked) continue;

        const remaining = parseFloat(node.quantity) - parseFloat(node.pickedQuantity || 0);
        if (remaining <= 0) continue;

        if (node.item.lotTracking) {
          // For lot-tracked items, use the pick engine
          const result = await fetchQuery(relayEnvironment, SUGGEST_PICKS_QUERY, {
            itemId: node.item.id,
            quantityNeeded: remaining,
          });

          const suggestions = result.suggestPicks || [];
          if (suggestions.length > 0) {
            const pick = suggestions[0];
            newPickQuantities[node.id] = {
              quantity: String(pick.quantity),
              lotId: pick.lotId,
              lotNumber: pick.lotNumber,
              warehouseLocationName: pick.warehouseLocationName || '',
            };
            pickedCount++;
          }
        } else {
          // For non-lot-tracked items, auto-fill the remaining quantity
          newPickQuantities[node.id] = {
            quantity: String(remaining),
            lotId: '',
            lotNumber: '',
            warehouseLocationName: '',
          };
          pickedCount++;
        }
      }

      this.setState({ pickQuantities: newPickQuantities, autoPickLoading: false });

      if (pickedCount > 0) {
        Actions.alertNotification(i18n.t('Auto-pick filled {{count}} item(s). Review and click Confirm Picks.', { count: pickedCount }), 'success');
      } else {
        Actions.alertNotification(i18n.t('No items available for auto-pick. Ensure lot-tracked items have lots with inventory.'), 'warning');
      }
    } catch (e) {
      this.setState({ autoPickLoading: false });
      Actions.alertNotification(i18n.t('Error loading auto-pick suggestions.'), 'error');
    }
  };

  handlePickQuantityChange = (shipmentItemId: string, field: string, value: string) => {
    this.setState(prev => ({
      pickQuantities: {
        ...prev.pickQuantities,
        [shipmentItemId]: {
          ...(prev.pickQuantities[shipmentItemId] || { quantity: '', lotId: '' }),
          [field]: value,
        },
      },
    }));
  };

  handleSubmitPicks = (shipmentId: string, itemEdges: Array<Object>) => {
    const { pickQuantities } = this.state;
    const picks = [];

    for (const edge of itemEdges) {
      const node = edge.node;
      const pickData = pickQuantities[node.id];
      if (pickData && pickData.quantity && parseFloat(pickData.quantity) > 0) {
        picks.push({
          shipmentItemId: node.id,
          lotId: pickData.lotId || null,
          quantity: parseFloat(pickData.quantity),
        });
      }
    }

    if (picks.length === 0) {
      Actions.alertNotification(i18n.t('Please enter pick quantities.'), 'error');
      return;
    }

    PickShipmentItemsMutation.commit({
      variables: { input: { shipmentId, picks } },
    })
      .then(() => {
        Actions.alertNotification(i18n.t('Items picked successfully.'), 'success');
        this.setState(prev => ({ pickingMode: false, pickQuantities: {}, refreshKey: prev.refreshKey + 1 }));
      })
      .catch(() => {
        Actions.alertNotification(i18n.t('Error picking items.'), 'error');
      });
  };

  render() {
    const { match, history } = this.props;
    const { editModalOpen, addItemModalOpen, pickingMode, pickQuantities, autoPickLoading, refreshKey, upsRates, upsRatesLoading, upsLabelLoading, upsTrackingLoading } = this.state;

    return (
      <QueryRenderer
        key={refreshKey}
        environment={relayEnvironment}
        cacheConfig={{ force: true }}
        query={graphql`
          query ShipmentQuery($id: ID) {
            shipment(id: $id) {
              id
              shipmentNumber
              status
              carrierName
              trackingNumber
              shippingMethod
              shippingAddressLine1
              shippingAddressLine2
              shippingAddressLine3
              shippingAddressCity
              shippingAddressState
              shippingAddressZip
              estimatedShipDate
              actualShipDate
              estimatedDeliveryDate
              actualDeliveryDate
              notes
              packageWeight
              packageLength
              packageWidth
              packageHeight
              shippingCost
              serviceType
              serviceName
              shippingLabelUrl
              upsShipmentId
              trackingEvents {
                id
                status
                description
                location
                eventTimestamp
              }
              order {
                id
                orderNumber
                status
                customer {
                  id
                  name
                }
              }
              customer {
                id
                name
              }
              items(first: 1000) {
                edges {
                  node {
                    id
                    quantity
                    description
                    item {
                      id
                      name
                      itemNumber
                      lotTracking
                      quantityUOM {
                        id
                        symbol
                      }
                    }
                    createdAt
                    serials {
                      id
                      serialNumber
                    }
                    picks {
                      id
                      quantity
                      pickedAt
                      lot {
                        id
                        lotNumber
                      }
                      pickedBy {
                        id
                        firstName
                        lastName
                      }
                    }
                    pickedQuantity
                    fullyPicked
                  }
                }
              }
              updatedAt
              createdAt
            }
            viewer {
              id
              permissions
              company {
                id
                name
                addressLine1
                addressLine2
                addressCity
                addressState
                addressZip
                addressCountry
                phone
              }
            }
          }
        `}
        variables={{ id: match.params.id }}
        render={({ props }) => {
          if (!props) return <Page><Loader /></Page>;

          const { shipment, viewer } = props;
          const canEdit = viewer.permissions.includes('UPDATE_SHIPMENT');
          const canDelete = viewer.permissions.includes('DELETE_SHIPMENT');

          const itemEdges = shipment.items?.edges || [];
          this.currentItemEdges = itemEdges;
          const existingItemIds = itemEdges.map(e => e.node.item.id);

          const contextMenuItems = [];
          if (canEdit) {
            contextMenuItems.push({
              label: i18n.t('Edit Shipment'),
              onClick: () => this.setState({ editModalOpen: true }),
            });
          }
          if (canDelete) {
            contextMenuItems.push({
              label: i18n.t('Delete Shipment'),
              onClick: () => this.handleDelete(shipment.id),
              destructive: true,
            });
          }

          return (
            <Page>
              <PageHeader
                title={`${i18n.t('Shipment')} ${shipment.shipmentNumber}`}
                backLabel={i18n.t('Shipments')}
                backTo="/shipments"
                contextMenuItems={contextMenuItems}
              />
              <PrintWrapper>
              <PrintOnly>
                <PrintPage>
                  <PrintDocHeader>
                    <PrintDocHeaderTitle>
                      <Logo style={{ width: 100, height: 'auto', display: 'block', marginBottom: 4 }} />
                      {i18n.t('Packing Slip')}
                    </PrintDocHeaderTitle>
                    <PrintDocHeaderCompany>{viewer && viewer.company && viewer.company.name}</PrintDocHeaderCompany>
                  </PrintDocHeader>

                  <PrintInfoGrid>
                    <PrintInfoColumn>
                      <PrintInfoRow>
                        <PrintInfoLabel>{i18n.t('Shipment #')}</PrintInfoLabel>
                        <div>{shipment.shipmentNumber}</div>
                      </PrintInfoRow>
                      <PrintInfoRow>
                        <PrintInfoLabel>{i18n.t('Date')}</PrintInfoLabel>
                        <div>{shipment.actualShipDate ? moment(shipment.actualShipDate).format('MM/DD/YYYY') : moment(shipment.createdAt).format('MM/DD/YYYY')}</div>
                      </PrintInfoRow>
                      <PrintInfoRow>
                        <PrintInfoLabel>{i18n.t('Delivery Date')}</PrintInfoLabel>
                        <div>
                          {shipment.estimatedDeliveryDate
                            ? moment(shipment.estimatedDeliveryDate).format('MM/DD/YYYY')
                            : shipment.actualDeliveryDate
                              ? moment(shipment.actualDeliveryDate).format('MM/DD/YYYY')
                              : '—'}
                        </div>
                      </PrintInfoRow>
                      <PrintInfoRow>
                        <PrintInfoLabel>{i18n.t('Customer')}</PrintInfoLabel>
                        <div>{shipment.customer.name}</div>
                      </PrintInfoRow>
                      {(shipment.shippingAddressLine1 || shipment.shippingAddressCity) && (
                        <PrintInfoRow>
                          <PrintInfoLabel>{i18n.t('Ship To')}</PrintInfoLabel>
                          <div>
                            {[shipment.shippingAddressLine1, shipment.shippingAddressLine2, shipment.shippingAddressLine3].filter(Boolean).map((line, i) => <div key={i}>{line}</div>)}
                            {(shipment.shippingAddressCity || shipment.shippingAddressState || shipment.shippingAddressZip) && (
                              <div>{[shipment.shippingAddressCity, shipment.shippingAddressState, shipment.shippingAddressZip].filter(Boolean).join(', ')}</div>
                            )}
                          </div>
                        </PrintInfoRow>
                      )}
                      <PrintInfoRow>
                        <PrintInfoLabel>{i18n.t('Order #')}</PrintInfoLabel>
                        <div>{shipment.order.orderNumber}</div>
                      </PrintInfoRow>
                      {shipment.shippingMethod && (
                        <PrintInfoRow>
                          <PrintInfoLabel>{i18n.t('Shipping Method')}</PrintInfoLabel>
                          <div>{shipment.shippingMethod}</div>
                        </PrintInfoRow>
                      )}
                      {shipment.carrierName && (
                        <PrintInfoRow>
                          <PrintInfoLabel>{i18n.t('Carrier')}</PrintInfoLabel>
                          <div>{shipment.carrierName}</div>
                        </PrintInfoRow>
                      )}
                      {shipment.trackingNumber && (
                        <PrintInfoRow>
                          <PrintInfoLabel>{i18n.t('Tracking #')}</PrintInfoLabel>
                          <div>{shipment.trackingNumber}</div>
                        </PrintInfoRow>
                      )}
                    </PrintInfoColumn>
                    {viewer && viewer.company && (
                      <PrintInfoColumn>
                        <PrintInfoRow>
                          <PrintInfoLabel>{i18n.t('Supplier')}</PrintInfoLabel>
                          <div>{viewer.company.name}</div>
                        </PrintInfoRow>
                        {(viewer.company.addressLine1 || viewer.company.addressCity) && (
                          <PrintInfoRow>
                            <PrintInfoLabel>{i18n.t('Address')}</PrintInfoLabel>
                            <div>
                              {viewer.company.addressLine1 && <div>{viewer.company.addressLine1}</div>}
                              {viewer.company.addressLine2 && <div>{viewer.company.addressLine2}</div>}
                              {(viewer.company.addressCity || viewer.company.addressState || viewer.company.addressZip) && (
                                <div>{[viewer.company.addressCity, viewer.company.addressState, viewer.company.addressZip].filter(Boolean).join(', ')}</div>
                              )}
                              {viewer.company.addressCountry && <div>{viewer.company.addressCountry}</div>}
                            </div>
                          </PrintInfoRow>
                        )}
                        {viewer.company.phone && (
                          <PrintInfoRow>
                            <PrintInfoLabel>{i18n.t('Phone')}</PrintInfoLabel>
                            <div>{viewer.company.phone}</div>
                          </PrintInfoRow>
                        )}
                      </PrintInfoColumn>
                    )}
                  </PrintInfoGrid>

                  <PrintItemTable>
                    <thead>
                      <tr>
                        <th style={{ width: '5%' }}>#</th>
                        <th style={{ textAlign: 'left' }}>{i18n.t('Part #')}</th>
                        <th style={{ textAlign: 'left' }}>{i18n.t('Description')}</th>
                        <th>{i18n.t('Qty Ordered')}</th>
                        <th>{i18n.t('Qty Shipped')}</th>
                        <th style={{ textAlign: 'left' }}>{i18n.t('Serial Numbers')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemEdges.map((edge, idx) => (
                        <tr key={edge.node.id}>
                          <td>{idx + 1}</td>
                          <td>{edge.node.item.itemNumber || edge.node.item.name}</td>
                          <td>{edge.node.description || edge.node.item.name}</td>
                          <td style={{ textAlign: 'center' }}>{edge.node.quantity} {edge.node.item.quantityUOM?.symbol || ''}</td>
                          <td style={{ textAlign: 'center' }}>{edge.node.pickedQuantity || 0} {edge.node.item.quantityUOM?.symbol || ''}</td>
                          <td>{edge.node.serials && edge.node.serials.length > 0 ? edge.node.serials.map(s => s.serialNumber).join(', ') : ''}</td>
                        </tr>
                      ))}
                      {itemEdges.length === 0 && (
                        <tr>
                          <td colSpan={6} style={{ textAlign: 'center', color: '#999' }}>{i18n.t('No items')}</td>
                        </tr>
                      )}
                    </tbody>
                  </PrintItemTable>

                  {shipment.notes && (
                    <PrintNotes>
                      <PrintInfoLabel>{i18n.t('Notes')}</PrintInfoLabel>
                      <div>{shipment.notes}</div>
                    </PrintNotes>
                  )}

                  <PrintSignatureGrid>
                    <PrintSignatureColumn>
                      <PrintSignatureLine>
                        <label>{i18n.t('Picked by:')}</label>
                        <PrintSignatureUnderline />
                      </PrintSignatureLine>
                      <PrintSignatureLine>
                        <label>{i18n.t('Packed by:')}</label>
                        <PrintSignatureUnderline />
                      </PrintSignatureLine>
                      <PrintSignatureLine>
                        <label>{i18n.t('Checked by:')}</label>
                        <PrintSignatureUnderline />
                      </PrintSignatureLine>
                      <PrintSignatureLine>
                        <label>{i18n.t('Date:')}</label>
                        <PrintSignatureUnderline />
                      </PrintSignatureLine>
                      <PrintSignatureLine>
                        <label>{i18n.t('Weight:')}</label>
                        <PrintSignatureUnderline />
                      </PrintSignatureLine>
                    </PrintSignatureColumn>
                    <PrintSignatureColumn>
                      <PrintSignatureLine>
                        <label>{i18n.t('Received by:')}</label>
                        <PrintSignatureUnderline />
                      </PrintSignatureLine>
                      <PrintSignatureLine>
                        <label>{i18n.t('Date:')}</label>
                        <PrintSignatureUnderline />
                      </PrintSignatureLine>
                    </PrintSignatureColumn>
                  </PrintSignatureGrid>

                  <PrintDocFooter>
                    <span>{i18n.t('Created')} {moment(shipment.createdAt).format('MM/DD/YYYY')}</span>
                  </PrintDocFooter>
                </PrintPage>
              </PrintOnly>

              <NoPrint>
              <Content>
                {canEdit && (
                  <ActionBar>
                    {shipment.status !== 'SHIPPED' && shipment.status !== 'DELIVERED' && (
                      <Button
                        theme="blue"
                        onClick={this.handleTogglePicking}
                      >
                        {pickingMode ? i18n.t('Cancel Picking') : i18n.t('Pick Items')}
                      </Button>
                    )}
                    {pickingMode && (
                      <Button
                        theme="blue"
                        loading={autoPickLoading}
                        onClick={() => this.handleAutoPick(itemEdges)}
                      >
                        {i18n.t('Auto Pick')}
                      </Button>
                    )}
                    {shipment.status !== 'SHIPPED' && shipment.status !== 'DELIVERED' && (
                      <Button
                        theme="green"
                        onClick={() => this.handleMarkShipped(shipment)}
                      >
                        {i18n.t('Mark as Shipped')}
                      </Button>
                    )}
                    <Button
                      theme="grey"
                      onClick={this.handlePrintPackingSlip}
                    >
                      {i18n.t('Print Packing Slip')}
                    </Button>
                    <Button
                      theme="blue"
                      onClick={() => this.handleEmailShipment(shipment.id)}
                    >
                      {i18n.t('Email Packing Slip')}
                    </Button>
                  </ActionBar>
                )}

                {pickingMode && (
                  <BarcodeScannerInput
                    onScan={this.handleBarcodeScan}
                    onScanNotFound={code =>
                      Actions.alertNotification(
                        i18n.t('Barcode "{{code}}" not found in the system.', { code }),
                        'warning',
                      )
                    }
                    placeholder={i18n.t('Scan item or lot barcode...')}
                    autoFocus
                  />
                )}

                <Section>
                  <SectionTitle>{i18n.t('General')}</SectionTitle>
                  <DetailGrid>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Status')}</DetailLabel>
                      <DetailValue>
                        <ShipmentStatusPill status={shipment.status} />
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Order')}</DetailLabel>
                      <DetailValue
                        style={{ cursor: 'pointer', color: '#1890ff' }}
                        onClick={() => history.push(`/order/${shipment.order.id}`)}
                      >
                        {shipment.order.orderNumber}
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Customer')}</DetailLabel>
                      <DetailValue
                        style={{ cursor: 'pointer', color: '#1890ff' }}
                        onClick={() => history.push(`/customer/${shipment.customer.id}`)}
                      >
                        {shipment.customer.name}
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Shipping Method')}</DetailLabel>
                      <DetailValue>{shipment.shippingMethod || '—'}</DetailValue>
                    </DetailItem>
                  </DetailGrid>
                </Section>

                <Section>
                  <SectionTitle>{i18n.t('Carrier & Tracking')}</SectionTitle>
                  <DetailGrid>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Carrier')}</DetailLabel>
                      <DetailValue>{shipment.carrierName || '—'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Tracking Number')}</DetailLabel>
                      <DetailValue>{shipment.trackingNumber || '—'}</DetailValue>
                    </DetailItem>
                    {shipment.shippingCost && (
                      <DetailItem>
                        <DetailLabel>{i18n.t('Shipping Cost')}</DetailLabel>
                        <DetailValue>{getCurrencySymbol()}{parseFloat(shipment.shippingCost).toFixed(2)}</DetailValue>
                      </DetailItem>
                    )}
                    {shipment.serviceName && (
                      <DetailItem>
                        <DetailLabel>{i18n.t('UPS Service')}</DetailLabel>
                        <DetailValue>{shipment.serviceName}</DetailValue>
                      </DetailItem>
                    )}
                    {shipment.shippingLabelUrl && (
                      <DetailItem>
                        <DetailLabel>{i18n.t('Shipping Label')}</DetailLabel>
                        <DetailValue>
                          <a href={shipment.shippingLabelUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>
                            {i18n.t('View / Print Label')}
                          </a>
                        </DetailValue>
                      </DetailItem>
                    )}
                  </DetailGrid>
                </Section>

                {/* Package Dimensions */}
                <Section>
                  <SectionTitle>{i18n.t('Package Info')}</SectionTitle>
                  <DetailGrid>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Weight (lbs)')}</DetailLabel>
                      {canEdit && !shipment.trackingNumber ? (
                        <input
                          type="number"
                          defaultValue={shipment.packageWeight || ''}
                          placeholder="1"
                          onBlur={e => this.handleUpdatePackageDimensions(shipment.id, 'packageWeight', e.target.value)}
                          style={{ width: 100, padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 14 }}
                          min="0"
                          step="0.1"
                        />
                      ) : (
                        <DetailValue>{shipment.packageWeight || '—'}</DetailValue>
                      )}
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Length (in)')}</DetailLabel>
                      {canEdit && !shipment.trackingNumber ? (
                        <input
                          type="number"
                          defaultValue={shipment.packageLength || ''}
                          placeholder="10"
                          onBlur={e => this.handleUpdatePackageDimensions(shipment.id, 'packageLength', e.target.value)}
                          style={{ width: 100, padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 14 }}
                          min="0"
                          step="0.1"
                        />
                      ) : (
                        <DetailValue>{shipment.packageLength || '—'}</DetailValue>
                      )}
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Width (in)')}</DetailLabel>
                      {canEdit && !shipment.trackingNumber ? (
                        <input
                          type="number"
                          defaultValue={shipment.packageWidth || ''}
                          placeholder="10"
                          onBlur={e => this.handleUpdatePackageDimensions(shipment.id, 'packageWidth', e.target.value)}
                          style={{ width: 100, padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 14 }}
                          min="0"
                          step="0.1"
                        />
                      ) : (
                        <DetailValue>{shipment.packageWidth || '—'}</DetailValue>
                      )}
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Height (in)')}</DetailLabel>
                      {canEdit && !shipment.trackingNumber ? (
                        <input
                          type="number"
                          defaultValue={shipment.packageHeight || ''}
                          placeholder="10"
                          onBlur={e => this.handleUpdatePackageDimensions(shipment.id, 'packageHeight', e.target.value)}
                          style={{ width: 100, padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 14 }}
                          min="0"
                          step="0.1"
                        />
                      ) : (
                        <DetailValue>{shipment.packageHeight || '—'}</DetailValue>
                      )}
                    </DetailItem>
                  </DetailGrid>
                </Section>

                {/* UPS Rate Shopping */}
                {canEdit && !shipment.trackingNumber && shipment.status !== 'SHIPPED' && shipment.status !== 'DELIVERED' && (
                  <Section>
                    <SectionTitle>{i18n.t('UPS Rate Shopping')}</SectionTitle>
                    <div style={{ marginBottom: 12 }}>
                      <Button
                        theme="blue"
                        size="small"
                        loading={upsRatesLoading}
                        onClick={() => this.handleGetUpsRates(shipment.id)}
                      >
                        {i18n.t('Get UPS Rates')}
                      </Button>
                    </div>
                    {upsRates.length > 0 && (
                      <ItemsTable>
                        <thead>
                          <tr>
                            <Th>{i18n.t('Service')}</Th>
                            <Th style={{ textAlign: 'right' }}>{i18n.t('Cost')}</Th>
                            <Th style={{ textAlign: 'center' }}>{i18n.t('Est. Days')}</Th>
                            <Th style={{ width: 120 }} />
                          </tr>
                        </thead>
                        <tbody>
                          {upsRates.map(rate => (
                            <tr key={rate.serviceCode}>
                              <Td>{rate.serviceName}</Td>
                              <Td style={{ textAlign: 'right', fontWeight: 600 }}>
                                {getCurrencySymbol()}{parseFloat(rate.totalCharge).toFixed(2)}
                              </Td>
                              <Td style={{ textAlign: 'center' }}>
                                {rate.estimatedDays || '—'}
                              </Td>
                              <Td>
                                <Button
                                  theme="green"
                                  size="small"
                                  loading={upsLabelLoading}
                                  onClick={() => this.handlePurchaseLabel(shipment.id, rate.serviceCode)}
                                >
                                  {i18n.t('Buy Label')}
                                </Button>
                              </Td>
                            </tr>
                          ))}
                        </tbody>
                      </ItemsTable>
                    )}
                  </Section>
                )}

                {/* UPS Tracking Timeline */}
                {shipment.trackingNumber && shipment.carrierName === 'UPS' && (
                  <Section>
                    <SectionTitle>{i18n.t('UPS Tracking')}</SectionTitle>
                    <div style={{ marginBottom: 12 }}>
                      <Button
                        theme="blue"
                        size="small"
                        loading={upsTrackingLoading}
                        onClick={() => this.handleRefreshTracking(shipment.id)}
                      >
                        {i18n.t('Refresh Tracking')}
                      </Button>
                    </div>
                    {shipment.trackingEvents && shipment.trackingEvents.length > 0 ? (
                      <div style={{ borderLeft: '3px solid #1890ff', paddingLeft: 16 }}>
                        {shipment.trackingEvents.map((evt, idx) => (
                          <div key={evt.id} style={{ marginBottom: idx < shipment.trackingEvents.length - 1 ? 16 : 0, position: 'relative' }}>
                            <div style={{
                              position: 'absolute',
                              left: -24,
                              top: 4,
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              backgroundColor: idx === 0 ? '#1890ff' : '#d9d9d9',
                              border: '2px solid #fff',
                            }} />
                            <div style={{ fontWeight: idx === 0 ? 600 : 400, fontSize: 14, color: '#333' }}>
                              {evt.status}
                            </div>
                            <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>
                              {evt.description}
                            </div>
                            <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
                              {evt.location && <span>{evt.location} &middot; </span>}
                              {evt.eventTimestamp && moment(evt.eventTimestamp).format('MM/DD/YYYY hh:mm A')}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <DetailValue style={{ color: '#999' }}>
                        {i18n.t('No tracking events yet. Click Refresh Tracking to fetch from UPS.')}
                      </DetailValue>
                    )}
                  </Section>
                )}

                <Section>
                  <SectionTitle>{i18n.t('Dates')}</SectionTitle>
                  <DetailGrid>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Est. Ship Date')}</DetailLabel>
                      <DetailValue>
                        {shipment.estimatedShipDate
                          ? moment(shipment.estimatedShipDate).format('MM/DD/YYYY')
                          : '—'}
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Actual Ship Date')}</DetailLabel>
                      <DetailValue>
                        {shipment.actualShipDate
                          ? moment(shipment.actualShipDate).format('MM/DD/YYYY')
                          : '—'}
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Est. Delivery Date')}</DetailLabel>
                      <DetailValue>
                        {shipment.estimatedDeliveryDate
                          ? moment(shipment.estimatedDeliveryDate).format('MM/DD/YYYY')
                          : '—'}
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Actual Delivery Date')}</DetailLabel>
                      <DetailValue>
                        {shipment.actualDeliveryDate
                          ? moment(shipment.actualDeliveryDate).format('MM/DD/YYYY')
                          : '—'}
                      </DetailValue>
                    </DetailItem>
                  </DetailGrid>
                </Section>

                <Section>
                  <SectionTitle>{i18n.t('Shipping Address')}</SectionTitle>
                  <DetailGrid>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Address')}</DetailLabel>
                      <DetailValue>
                        {[
                          shipment.shippingAddressLine1,
                          shipment.shippingAddressLine2,
                          shipment.shippingAddressLine3,
                        ]
                          .filter(Boolean)
                          .join(', ') || '—'}
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('City')}</DetailLabel>
                      <DetailValue>{shipment.shippingAddressCity || '—'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('State')}</DetailLabel>
                      <DetailValue>{shipment.shippingAddressState || '—'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Zip')}</DetailLabel>
                      <DetailValue>{shipment.shippingAddressZip || '—'}</DetailValue>
                    </DetailItem>
                  </DetailGrid>
                </Section>

                {shipment.notes && (
                  <Section>
                    <SectionTitle>{i18n.t('Notes')}</SectionTitle>
                    <DetailValue>{shipment.notes}</DetailValue>
                  </Section>
                )}

                <Section>
                  <ItemsHeader>
                    <SectionTitle style={{ marginBottom: 0 }}>
                      {i18n.t('Items')} ({itemEdges.length})
                    </SectionTitle>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {canEdit && shipment.status !== 'SHIPPED' && shipment.status !== 'DELIVERED' && (
                        <Button
                          theme="blue"
                          size="small"
                          onClick={() => this.setState({ addItemModalOpen: true })}
                        >
                          {i18n.t('+ Add Item')}
                        </Button>
                      )}
                    </div>
                  </ItemsHeader>
                  {itemEdges.length > 0 ? (
                    <ItemsTable>
                      <thead>
                        <tr>
                          <Th>{i18n.t('Item')}</Th>
                          <Th>{i18n.t('Part #')}</Th>
                          <Th style={{ textAlign: 'right' }}>{i18n.t('Quantity')}</Th>
                          <Th style={{ textAlign: 'right' }}>{i18n.t('Picked')}</Th>
                          <Th>{i18n.t('Serial Numbers')}</Th>
                          <Th>{i18n.t('Description')}</Th>
                          {pickingMode && <Th>{i18n.t('Lot')}</Th>}
                          {pickingMode && <Th>{i18n.t('Location')}</Th>}
                          {pickingMode && <Th>{i18n.t('Pick Qty')}</Th>}
                          {canEdit && !pickingMode && shipment.status !== 'SHIPPED' && shipment.status !== 'DELIVERED' && (
                            <Th style={{ width: 80 }} />
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {itemEdges.map(edge => {
                          const remaining = parseFloat(edge.node.quantity) - parseFloat(edge.node.pickedQuantity || 0);
                          const pickData = pickQuantities[edge.node.id] || { quantity: '', lotId: '', lotNumber: '', warehouseLocationName: '' };
                          return (
                            <tr key={edge.node.id} style={edge.node.fullyPicked ? { backgroundColor: '#f0fff0' } : {}}>
                              <Td>{edge.node.item.name}</Td>
                              <Td>{edge.node.item.itemNumber}</Td>
                              <Td style={{ textAlign: 'right' }}>
                                {edge.node.quantity} {edge.node.item.quantityUOM?.symbol || ''}
                              </Td>
                              <Td style={{ textAlign: 'right', color: edge.node.fullyPicked ? '#00bd83' : '#e67e22' }}>
                                {edge.node.pickedQuantity || 0} {edge.node.item.quantityUOM?.symbol || ''}
                                {edge.node.fullyPicked && ' ✓'}
                              </Td>
                              <Td>
                                {edge.node.serials && edge.node.serials.length > 0
                                  ? edge.node.serials.map(s => s.serialNumber).join(', ')
                                  : '—'}
                              </Td>
                              <Td>{edge.node.description || '—'}</Td>
                              {pickingMode && (
                                <Td>
                                  {edge.node.item.lotTracking ? (
                                    <div>
                                      <input
                                        type="text"
                                        placeholder={i18n.t('Lot ID')}
                                        value={pickData.lotId}
                                        onChange={e => this.handlePickQuantityChange(edge.node.id, 'lotId', e.target.value)}
                                        style={{ width: 100, padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 13 }}
                                      />
                                      {pickData.lotNumber && (
                                        <div style={{ fontSize: 11, color: '#1890ff', marginTop: 2 }}>
                                          {pickData.lotNumber}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <span style={{ color: '#999' }}>—</span>
                                  )}
                                </Td>
                              )}
                              {pickingMode && (
                                <Td>
                                  {pickData.warehouseLocationName ? (
                                    <span style={{ fontSize: 12, color: '#666' }}>{pickData.warehouseLocationName}</span>
                                  ) : (
                                    <span style={{ color: '#999' }}>—</span>
                                  )}
                                </Td>
                              )}
                              {pickingMode && (
                                <Td>
                                  {!edge.node.fullyPicked ? (
                                    <input
                                      type="number"
                                      placeholder={String(remaining)}
                                      value={pickData.quantity}
                                      onChange={e => this.handlePickQuantityChange(edge.node.id, 'quantity', e.target.value)}
                                      style={{ width: 80, padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 13 }}
                                      min="0"
                                      max={remaining}
                                      step="any"
                                    />
                                  ) : (
                                    <span style={{ color: '#00bd83', fontWeight: 600 }}>{i18n.t('Done')}</span>
                                  )}
                                </Td>
                              )}
                              {canEdit && !pickingMode && shipment.status !== 'SHIPPED' && shipment.status !== 'DELIVERED' && (
                                <Td>
                                  <DeleteLink onClick={() => this.handleDeleteItem(edge.node.id)}>
                                    {i18n.t('Remove')}
                                  </DeleteLink>
                                </Td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </ItemsTable>
                  ) : (
                    <DetailValue style={{ color: '#999' }}>
                      {i18n.t('No items added to this shipment yet.')}
                    </DetailValue>
                  )}
                  {pickingMode && itemEdges.length > 0 && (
                    <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
                      <Button
                        theme="green"
                        size="small"
                        width="auto"
                        onClick={() => this.handleSubmitPicks(shipment.id, itemEdges)}
                      >
                        {i18n.t('Confirm Picks')}
                      </Button>
                      <Button
                        theme="grey"
                        size="small"
                        width="auto"
                        onClick={this.handleTogglePicking}
                      >
                        {i18n.t('Cancel')}
                      </Button>
                    </div>
                  )}

                  {/* Pick History */}
                  {itemEdges.some(e => e.node.picks && e.node.picks.length > 0) && (
                    <div style={{ marginTop: 24 }}>
                      <SectionTitle>{i18n.t('Pick History')}</SectionTitle>
                      <ItemsTable>
                        <thead>
                          <tr>
                            <Th>{i18n.t('Item')}</Th>
                            <Th>{i18n.t('Lot')}</Th>
                            <Th style={{ textAlign: 'right' }}>{i18n.t('Qty Picked')}</Th>
                            <Th>{i18n.t('Picked By')}</Th>
                            <Th>{i18n.t('Picked At')}</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {itemEdges.flatMap(edge =>
                            (edge.node.picks || []).map(pick => (
                              <tr key={pick.id}>
                                <Td>{edge.node.item.name}</Td>
                                <Td>{pick.lot ? pick.lot.lotNumber : '—'}</Td>
                                <Td style={{ textAlign: 'right' }}>{pick.quantity}</Td>
                                <Td>
                                  {pick.pickedBy
                                    ? `${pick.pickedBy.firstName} ${pick.pickedBy.lastName}`
                                    : '—'}
                                </Td>
                                <Td>{pick.pickedAt ? moment(pick.pickedAt).format('MM/DD/YYYY hh:mm A') : '—'}</Td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </ItemsTable>
                    </div>
                  )}
                </Section>

                <Section>
                  <SectionTitle>{i18n.t('Timestamps')}</SectionTitle>
                  <DetailGrid>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Created')}</DetailLabel>
                      <DetailValue>
                        {moment(shipment.createdAt).format('MM/DD/YYYY hh:mm A')}
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Last Updated')}</DetailLabel>
                      <DetailValue>
                        {moment(shipment.updatedAt).format('MM/DD/YYYY hh:mm A')}
                      </DetailValue>
                    </DetailItem>
                  </DetailGrid>
                </Section>
              </Content>
              </NoPrint>
              </PrintWrapper>

              {editModalOpen && (
                <AddUpdateShipmentModal
                  onClose={() => this.setState({ editModalOpen: false })}
                  shipment={shipment}
                />
              )}

              {addItemModalOpen && (
                <AddShipmentItemModal
                  onClose={() => this.setState(prev => ({ addItemModalOpen: false, refreshKey: prev.refreshKey + 1 }))}
                  shipmentId={shipment.id}
                  existingItemIds={existingItemIds}
                />
              )}
            </Page>
          );
        }}
      />
    );
  }
}

export default Shipment;
